<template>
    <div>
        <filter-bar
            :filters-show-hide-config="filtersShowHideConfig"
            :filters-config="filtersConfig">
            <template #additional-left-filters>
                <slot name="additional-left-filters" />
            </template>
            <template
                v-if="showSearch"
                #right>
                <slot name="search">
                    <search v-model="search" />
                </slot>
            </template>
            <template
                v-if="showExpansionFilters"
                #expansion>
                <div class="expansion-filters-container">
                    <div class="expansion-filters">
                        <multiselect-labels-filter
                            v-if="showLabels"
                            :value="labels"
                            :available-labels="availableLabels"
                            :loading="loadingLabels"
                            @create-label="createLabel"
                            @edit-label="openEditModal"
                            @delete-label="openDeleteModal"
                            @input="updateExpansionFilters($event, 'labels')" />
                        <label-popover
                            v-if="showLabelPopover"
                            entity-type="Guide"
                            :available-labels="availableLabels"
                            :can-add-public="canAddPublicLabels && !disablePublicToggle"
                            :label-to-edit="labelToEdit"
                            @closed="onLabelPopoverClose" />
                        <delete-label
                            :label-to-delete="labelToDelete"
                            @closed="onDeleteModalClose" />
                        <multiselect-groups-filter
                            v-if="!isPendoFree && showExpansionFilterConfig.showProductAreas"
                            :edit="showEditGroup"
                            @create-group="createGroup"
                            @input="onGroupUpdate"
                            @edit-group="editGroup" />
                        <pendo-guide-category-multiselect
                            v-if="showExpansionFilterConfig.showCategories"
                            :categories="guidesCategoryOptions"
                            :selected-categories="categories"
                            :select-multiple="true"
                            :is-filtering="true"
                            :empty-option="NO_GUIDE_CATEGORY_OPTION"
                            :allow-create-category="isAdmin"
                            :allow-delete-category="isAdmin"
                            :allow-edit-category="isAdmin"
                            @edit="handleCategoryModalOpen(MODALS.upsertCategory, $event)"
                            @delete="handleCategoryModalOpen(MODALS.deleteCategory, $event)"
                            @create="handleCategoryModalOpen(MODALS.upsertCategory)"
                            @input="updateSubSpecificFilters($event, 'categories')">
                            <template #trigger>
                                <pendo-data-measure-trigger />
                            </template>
                        </pendo-guide-category-multiselect>
                        <pendo-multiselect-filter
                            v-if="showExpansionFilterConfig.showStatuses.show"
                            :value="statuses"
                            :options="filterChoices.statuses"
                            label-key="name"
                            :entity-name="$t('guides.dropdowns.statuses')"
                            :all-label="$t('guides.dropdowns.all_statuses')"
                            @input="updateExpansionFilters($event, 'statuses')" />
                        <multiselect-pages-filter
                            v-if="showExpansionFilterConfig.showPages"
                            :value="pages"
                            @input="updateExpansionFilters($event, 'pages')" />
                        <pendo-multiselect-filter
                            v-if="showExpansionFilterConfig.showActivations"
                            :value="activations"
                            :options="filterChoices.activations"
                            :entity-name="$t('guides.dropdowns.activations')"
                            :all-label="$t('guides.dropdowns.all_activations')"
                            @input="updateExpansionFilters($event, 'activations')" />
                        <pendo-multiselect-filter
                            v-if="showExpansionFilterConfig.showExpirations"
                            :value="expirations"
                            :options="filterChoices.expirations"
                            :entity-name="$t('guides.dropdowns.expirations')"
                            :all-label="$t('guides.dropdowns.all_expirations')"
                            @input="updateExpansionFilters($event, 'expirations')" />
                        <pendo-multiselect-filter
                            v-if="!restrictP1Access && showExpansionFilterConfig.showGuideTypes"
                            :value="guideTypes"
                            :options="filterChoices.guideTypes"
                            :entity-name="$t('guides.dropdowns.guide_types')"
                            :all-label="$t('guides.dropdowns.all_guide_types')"
                            @input="updateExpansionFilters($event, 'guideTypes')" />
                        <multiselect-created-by-filter
                            v-if="showExpansionFilterConfig.showCreators"
                            :value="creators"
                            :current-user="user"
                            :guides="guideList"
                            :loading="loading"
                            @input="updateSubSpecificFilters($event, 'creators')" />
                        <pendo-multiselect-filter
                            v-if="showExpansionFilterConfig.showDeviceTypes"
                            :value="deviceTypes"
                            :options="filterChoices.deviceTypes"
                            :entity-name="$t('guides.dropdowns.device_types')"
                            :all-label="$t('guides.dropdowns.all_device_types')"
                            @input="updateExpansionFilters($event, 'deviceTypes')" />
                        <pendo-multiselect-filter
                            v-if="showExpansionFilterConfig.showManagedIn && filterChoices.managedIn.length"
                            :value="managedIn"
                            :options="filterChoices.managedIn"
                            :show-all-label="false"
                            :entity-name="$t('guides.dropdowns.managedIn')"
                            @input="updateExpansionFilters($event, 'managedIn')" />
                            <!-- Does managed in need to change like <multiselect-created-by-filter /> -->
                    </div>
                    <div class="clear-filters">
                        <pendo-button
                            v-if="areFiltersSet"
                            theme="app"
                            type="subtle"
                            :label="$t('guides.actions.buttons.clearFilters')"
                            @click="clearFiltersAndGroups" />
                    </div>
                </div>
            </template>
        </filter-bar>
        <group-modal
            :visible="groupModalVisible"
            :edit="showEditGroup"
            :group="group"
            :group-id="groupId"
            @close="groupModalVisible = false" />
    </div>
</template>

<script>
import { mapGetters, mapActions, mapState } from 'vuex';
import { PendoButton, PendoMultiselectFilter, PendoGuideCategoryMultiselect, PendoDataMeasureTrigger } from '@pendo/components';
import LabelActionManager from '@/common/labels/mixins/label-action-manager.mixin.js';
import FilterBar from '@/common/filter-bar/filter-bar.vue';
import MultiselectCreatedByFilter from '@/common/filter-bar/filters/multiselect-created-by-filter/multiselect-created-by-filter.vue';
import MultiselectGroupsFilter from '@/common/filter-bar/filters/multiselect-groups-filter/multiselect-groups-filter.vue';
import MultiselectPagesFilter from '@/common/filter-bar/filters/multiselect-pages-filter/multiselect-pages-filter.vue';
import MultiselectLabelsFilter from '@/common/filter-bar/filters/multiselect-labels-filter/multiselect-labels-filter.vue';
import LabelPopover from '@/common/labels/label-popover/label-popover.vue';
import DeleteLabel from '@/common/labels/delete-label/delete-label.vue';
import Search from '@/common/search/search.vue';
import GroupModal from '@/groups/group-modal/group-modal.vue';
import { getFilterChoiceConfig } from '../config/filter-choices.js';
import moment from '@/utils/moment-wrapper';
import keyBy from 'lodash/keyBy';
import some from 'lodash/some';
import cloneDeep from 'lodash/cloneDeep';
import get from 'lodash/get';
import { getNoGuideCategoryOption } from '@/common/guide-category-multiselect/guide-category-multiselect-cell.vue';
import { isNpsGuide } from '@/vuex/modules/guides/guides.utils';
import { MODALS } from '@/guides/constants/modals';

export default {
    name: 'GuideExpansionFilters',
    components: {
        DeleteLabel,
        LabelPopover,
        MultiselectCreatedByFilter,
        MultiselectGroupsFilter,
        MultiselectPagesFilter,
        MultiselectLabelsFilter,
        FilterBar,
        GroupModal,
        PendoButton,
        Search,
        PendoMultiselectFilter,
        PendoGuideCategoryMultiselect,
        PendoDataMeasureTrigger,
    },
    mixins: [LabelActionManager],
    props: {
        availableLabels: {
            type: Array,
            default: () => [],
        },
        expansionFiltersConfig: {
            type: Object,
            default: () => {},
        },
        canAddPublicLabels: {
            type: Boolean,
            default: false,
        },
        guideList: {
            type: Array,
            default: () => [],
        },
        loading: {
            type: Boolean,
            default: false,
        },
        loadingLabels: {
            type: Boolean,
            default: false,
        },
        restrictP1Access: {
            type: Boolean,
            default: false,
        },
        showLabels: {
            type: Boolean,
            default: false,
        },
        showSearch: {
            type: Boolean,
            default: true,
        },
        user: {
            type: Object,
            default: () => ({}),
        },
        filtersShowHideConfig: {
            type: Object,
            default: () => ({ starred: true }),
        },
        filtersConfig: {
            type: Object,
            default: () => ({
                filterType: 'guide',
            }),
        },
    },
    emits: ['onUpdateFilters', 'onUpdateGroupIds'],
    data () {
        return {
            localGuideListExpansionFilters: {},
            localGuideListSubSpecificFilters: {},
            loadingUserSettings: true,
            groupId: null,
            groupIds: [],
            groupModalVisible: false,
            showEditGroup: false,
            group: null,
            search: get(this, '$route.meta.search', ''),
            labels: [],
            statuses: [],
            pages: [],
            activations: [],
            expirations: [],
            guideTypes: [],
            creators: [],
            deviceTypes: [],
            categories: [],
            managedIn: [],
            NO_GUIDE_CATEGORY_OPTION: null,
            filterChoices: getFilterChoiceConfig(),
        };
    },
    computed: {
        ...mapState({
            groupIdFilter: (state) => state.groups.idFilter,
            groupsLoading: (state) => state.groups.fetching,
        }),
        ...mapGetters({
            settingByName: 'userSettings/settingByName',
            getFeatureFlag: 'subscription/getFeatureFlag',
            groupById: 'groups/groupById',
            storedUserSettingGroupFilter: 'groups/storedUserSettingGroupFilter',
            starredByType: 'globalFilters/starredByType',
            isPendoFree: 'subscription/isPendoFree',
            guideCategoriesList: 'guideCategories/list',
            guideCategoriesGroupedByMetaGroup: 'guideCategories/guideCategoriesGroupedByMetaGroup',
            hasProductLinePurchased: 'productLines/hasProductLinePurchased',
            productLineByType: 'productLines/productLineByType',
            isAdmin: 'auth/isAdmin',
        }),
        guidesCategoryOptions () {
            /*
             * Only showing web categories here only works based on two assumptions:
             * 1. Web and mobile categories are the same
             * 2. Filters work by filtering based on category names
             *
             * This is to support multi app subs
             */

            return this.guideCategoriesGroupedByMetaGroup('web');
        },
        showExpansionFilters () {
            return some(this.showExpansionFilterConfig, (filter) => {
                return filter?.show || filter === true;
            });
        },
        showExpansionFilterConfig () {
            return {
                showGroups: true,
                showPages: false,
                showStatuses: {
                    show: true,
                    hideOptionId: '',
                },
                showActivations: true,
                showCategories: true,
                showExpirations: true,
                showGuideTypes: true,
                showDeviceTypes: true,
                showCreators: true,
                showProductAreas: true,
                showManagedIn: true,
                ...this.expansionFiltersConfig,
            };
        },
        deviceTypeFilterFn () {
            const matchConditions = this.deviceTypes.map((deviceType) => {
                if (deviceType === 'Desktop and Mobile Web') {
                    deviceType = 'Desktop, Mobile Web';
                }

                return (row) => row.deviceType === deviceType;
            });

            return (row) => matchConditions.some((condition) => condition(row));
        },
        labelsFilterFn () {
            const labelIds = this.labels.map((label) => label.id);

            return (row) => {
                if (!row.labels) return true; // Wait for labels to load

                return row.labels?.some(({ id }) => labelIds.includes(id));
            };
        },
        statusFilterFn () {
            return (row) => this.statuses.some(({ id }) => row.state === id);
        },
        categoryFilterFn () {
            return (row) => this.categories.some(({ name }) => row.category.name === name);
        },
        pagesFilterFn () {
            return (row) => this.pages.some((page) => row.page.name === page);
        },
        productAreaFilterFn () {
            return (row) => this.groupIdFilter.some((groupId) => row.group.id === groupId);
        },
        managedInFilterFn () {
            return (row) => {
                let rowMatchesFilter = false;
                this.managedIn.forEach((entity) => {
                    if (entity.prop === 'surveyId' && isNpsGuide(row)) {
                        rowMatchesFilter = true;
                    }

                    if (row.attributes[entity.prop]) {
                        rowMatchesFilter = true;

                    }
                });

                return rowMatchesFilter;
            };
        },
        areFiltersSet () {
            const {
                showStatuses,
                showActivations,
                showCategories,
                showExpirations,
                showGuideTypes,
                showDeviceTypes,
                showCreators,
                showPages,
                showProductAreas,
                showManagedIn,
            } = this.showExpansionFilterConfig;

            return (
                (!!this.groupIdFilter.length && showProductAreas) ||
                (!!this.categories.length && showCategories) ||
                (!!this.activations.length && showActivations) ||
                (!!this.creators.length && showCreators) ||
                (!!this.deviceTypes.length && showDeviceTypes) ||
                (!!this.expirations.length && showExpirations) ||
                (!!this.guideTypes.length && showGuideTypes) ||
                (!!this.labels.length && this.showLabels) ||
                (!!this.statuses.length && showStatuses) ||
                (!!this.pages.length && showPages) ||
                (!!this.managedIn.length && showManagedIn)
            );
        },
        filters () {
            const {
                activations,
                categories,
                creators,
                deviceTypes,
                expirations,
                guideTypes,
                labels,
                statuses,
                groupIdFilter,
                pages,
                showLabels,
                managedIn,
                showExpansionFilterConfig,
            } = this;

            const {
                showStatuses,
                showActivations,
                showCategories,
                showExpirations,
                showGuideTypes,
                showDeviceTypes,
                showCreators,
                showPages,
                showProductAreas,
                showManagedIn,
            } = showExpansionFilterConfig;

            const filters = [
                {
                    prop: ['name', 'createdBy', 'lastUpdatedBy', 'id', 'category.name', 'description'],
                    value: this.search,
                },
                {
                    prop: 'activationMethod',
                    value:
                        showActivations && activations.length < this.filterChoices.activations.length
                            ? activations
                            : [],
                },
                {
                    prop: 'createdBy',
                    value: showCreators ? creators.map((creator) => creator.id || creator) : [],
                },
                {
                    prop: 'type',
                    value:
                        showGuideTypes && guideTypes.length < this.filterChoices.guideTypes.length
                            ? guideTypes.map((type) => type.id || type)
                            : [],
                },
                {
                    prop: 'starred',
                    value: this.starredByType(this.filtersConfig.filterType) ? [true] : [],
                },
            ];

            if (statuses.length && statuses.length !== this.filterChoices.statuses.length && showStatuses?.show) {
                filters.push({ filterFn: this.statusFilterFn });
            }

            // Start at 1 for empty option
            let guideCategoriesLength = 1;
            this.guidesCategoryOptions.forEach((option) => {
                guideCategoriesLength += option?.items.length;
            });

            if (categories.length && categories.length !== guideCategoriesLength && showCategories) {
                filters.push({ filterFn: this.categoryFilterFn });
            }

            if (pages.length && showPages) {
                filters.push({ filterFn: this.pagesFilterFn });
            }

            if (deviceTypes.length && showDeviceTypes) {
                filters.push({ filterFn: this.deviceTypeFilterFn });
            }

            if (labels.length && showLabels) {
                filters.push({ filterFn: this.labelsFilterFn });
            }

            if (groupIdFilter.length && showProductAreas) {
                filters.push({ filterFn: this.productAreaFilterFn });
            }

            if (managedIn.length && showManagedIn) {
                filters.push({ filterFn: this.managedInFilterFn });
            }

            const isExpired = (x) => x.expirationDate && moment(x.expirationDate) < moment();

            if (expirations.length === 1 && showExpirations) {
                if (expirations[0] === 'Active') {
                    filters.push({ filterFn: (x) => !isExpired(x) });
                } else if (expirations[0] === 'Expired') {
                    filters.push({ filterFn: isExpired });
                }
            }

            return filters;
        },
    },
    watch: {
        filters: {
            immediate: true,
            handler () {
                this.$emit('onUpdateFilters', this.filters);
            },
        },
        search: {
            immediate: true,
            handler () {
                if (this.$route) {
                    this.$route.meta.search = this.search;
                }
            },
        },
        guideCategoriesList (newList) {
            // Ensure only existing categories remain in the filter selections.
            const existingIds = new Set(newList.map(({ id }) => id));
            this.categories = this.categories.filter(({ id }) => existingIds.has(id));
        },
        availableLabels () {
            const labelsMap = keyBy(this.labels, 'id');
            this.labels = this.availableLabels.filter((label) => labelsMap[label.id]);
        },
    },
    async created () {
        this.MODALS = MODALS;
        this.NO_GUIDE_CATEGORY_OPTION = getNoGuideCategoryOption();

        await Promise.all([
            this.getStoredFilters(),
            this.loadAllMetaGroups(),
            this.loadAllGroups({ items: true }),
            this.loadAllGuideCategories({ items: true }),
        ]);

        this.onGroupUpdate(this.storedUserSettingGroupFilter);
        this.hideOptionFromStatusFilter();
    },
    methods: {
        ...mapActions({
            loadUserSetting: 'userSettings/loadOne',
            updateUserSetting: 'userSettings/update',
            loadAllGroups: 'groups/loadAll',
            clearGroupFilter: 'groups/clearFilter',
            loadAllMetaGroups: 'metaGroups/loadAll',
            loadAllGuideCategories: 'guideCategories/loadAll',
            openModal: 'guideModals/openModal',
        }),
        onSearchClear () {
            this.search = '';
        },
        async getStoredFilters () {
            this.loadingUserSettings = true;

            await Promise.all([
                this.loadUserSetting({ name: 'guideListExpansionFilters', namespaced: false }),
                this.loadUserSetting({ name: 'guideListSubSpecificFilters', namespaced: true }),
            ]);

            // load and set `guideListExpansionFilters` user settings (global)
            this.localGuideListExpansionFilters = cloneDeep(this.settingByName('guideListExpansionFilters', false));
            if (this.localGuideListExpansionFilters?.value) {
                this.setLocalFilters(this.localGuideListExpansionFilters.value);
            }

            // load and set `guideListSubSpecificFilters` user settings (sub-specific)
            this.localGuideListSubSpecificFilters = cloneDeep(this.settingByName('guideListSubSpecificFilters', true));
            if (this.localGuideListSubSpecificFilters?.value) {
                this.setLocalFilters(this.localGuideListSubSpecificFilters.value);
            }
            this.loadingUserSettings = false;
        },
        setLocalFilters (filters) {
            Object.entries(filters).forEach(([key, value]) => {
                // if the filter key does not exist in filter-choices.js, assume all values are valid
                if (!this.filterChoices[key]) {
                    this[key] = value;
                } else {
                    const filterChoices = this.filterChoices[key].map((choice) => choice.id || choice);
                    const availableValues = value.filter((val) => {
                        const id = val.id || val;

                        return filterChoices.includes(id);
                    });
                    this[key] = availableValues;
                }
            });
        },
        onGroupUpdate (newGroupIds) {
            this.$emit('onUpdateGroupIds', newGroupIds);
            window.pendo?.track('filters', {
                filterType: 'product-area-guide-list',
            });
        },
        async clearFiltersAndGroups () {
            this.clearGroupFilter();
            this.labels = [];
            this.statuses = [];
            this.pages = [];
            this.activations = [];
            this.categories = [];
            this.expirations = [];
            this.guideTypes = [];
            this.creators = [];
            this.deviceTypes = [];
            this.managedIn = [];
            this.localGuideListExpansionFilters = {};
            this.localGuideListSubSpecificFilters = {};

            await Promise.all([
                this.updateUserSetting({
                    name: 'guideListExpansionFilters',
                    namespaced: false,
                    value: undefined,
                    force: true,
                }),
                this.updateUserSetting({
                    name: 'guideListSubSpecificFilters',
                    namespaced: true,
                    value: undefined,
                    force: true,
                }),
            ]);
        },
        createGroup () {
            this.groupModalVisible = true;
            this.showEditGroup = false;
            this.group = {};
        },
        editGroup (group) {
            this.groupModalVisible = true;
            this.showEditGroup = true;
            this.groupId = group.id;
            this.group = this.groupById(group.id);
        },
        createLabel () {
            this.showLabelPopover = true;
        },
        handleCategoryModalOpen (modal, { id } = {}) {
            this.openModal({
                modal,
                props: {
                    categoryId: id || null,
                },
            });
        },
        hideOptionFromStatusFilter () {
            const {
                showStatuses: { hideOptionId },
            } = this.showExpansionFilterConfig;
            if (hideOptionId !== '') {
                this.filterChoices.statuses = this.filterChoices.statuses.filter(
                    (option) => option.id !== hideOptionId,
                );
                this.statuses = this.statuses.filter(({ id }) => id !== hideOptionId);
            }
        },
        async updateExpansionFilters (config, type) {
            this[type] = config;
            const settings = {
                name: 'guideListExpansionFilters',
                namespaced: false,
                value: { ...this.localGuideListExpansionFilters.value, [type]: config },
                force: true,
            };
            this.localGuideListExpansionFilters.value = settings.value;
            await this.updateUserSetting(settings);
            window.pendo?.track('filters', {
                filterType: `${type}-guide-list`,
            });
        },
        async updateSubSpecificFilters (config, type) {
            this[type] = config;
            const settings = {
                name: 'guideListSubSpecificFilters',
                namespaced: true,
                value: { ...this.localGuideListSubSpecificFilters.value, [type]: config },
                force: true,
            };
            this.localGuideListSubSpecificFilters.value = settings.value;
            await this.updateUserSetting(settings);
            window.pendo?.track('filters', {
                filterType: `${type}-guide-list`,
            });
        },
    },
};
</script>

<style lang="less" scoped>
.clear-filters {
    margin-left: auto;
    margin-right: 14px;
}

.expansion-filters-container {
    display: flex;
    width: 100%;
}

.expansion-filters {
    display: flex;
    flex-wrap: wrap;
}

.clear-search:hover {
    cursor: pointer;
}

</style>
